// src/components/Billing/BillingItemsTable.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Checkbox, Button,
  Typography, Box, IconButton
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CustomTextField from '../../CustomTextField';
import CustomDropdown from '../../../components/CustomDropdownWithPlus';
import {
  buildFinanceCodeDropdownOptions,
  hasEnabledBillFinanceCodes,
} from '../../../services/financeCodes';
import { clearAuthoritativePoSubtotal } from './billPrecision';

const money2 = (n) => (Number.isFinite(Number(n)) ? Number(n).toFixed(2) : '0.00');
const hasValue = (value) => value !== null && value !== undefined && value !== '';
const normalizeTaxSelection = (value) => {
  if (value === null || value === undefined || String(value).trim() === '') return '';
  return value;
};
const roundMoney = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? Number(num.toFixed(2)) : 0;
};

// Robustly extract a selected value from CustomDropdown
const pickSelected = (input) => {
  if (!input) return '';
  if (input?.target?.value != null) return input.target.value;          // native/select event
  if (typeof input === 'string' || typeof input === 'number') return input; // raw value
  if (input?.value != null) return input.value;                          // { value, label }
  if (input?.id != null) return input.id;                                // { id, name }
  return '';
};

export const applyFinanceCodeSelection = (row = {}, selectedOption = null, selectedValue = '', previousOption = null) => {
  const nextCode = selectedOption?.id ?? selectedOption?.value ?? selectedValue ?? '';
  const nextLabel = String(
    selectedOption?.label ?? selectedOption?.name ?? selectedOption?.displayName ?? selectedOption?.code ?? nextCode
  ).trim();
  const previousCode = String(row?.code ?? '').trim();
  const currentName = String(row?.itemName ?? '').trim();
  const previousLabel = String(previousOption?.label ?? previousOption?.name ?? previousOption?.displayName ?? previousCode).trim();
  const itemNameWasAutomatic = !currentName || currentName === previousCode || currentName === previousLabel;

  return {
    ...row,
    code: nextCode,
    itemName: itemNameWasAutomatic && nextLabel ? nextLabel : row?.itemName,
  };
};

const parsePercents = (label) => {
  if (!label) return [];
  const s = String(label);
  const matches = s.match(/([\d.]+)\s*%/g);
  if (matches) {
    return matches
      .map(m => Number(m.replace('%', '').trim()))
      .filter(n => Number.isFinite(n) && n >= 0);
  }
  // Avoid treating raw tax ids (e.g. "11") as 11%.
  if (/^\d+(\.\d+)?$/.test(s.trim())) return [];
  // fallback: split numbers out (e.g., "5 + 7")
  return s
    .split(/[^0-9.]+/g)
    .map(Number)
    .filter(n => Number.isFinite(n) && n >= 0);
};

const sumPct = (arr) => arr.reduce((a, b) => a + (Number(b) || 0), 0);
const taxIdEquals = (optionId, rawValue) => {
  const left = String(optionId ?? '').trim();
  const right = String(rawValue ?? '').trim();
  if (!left || !right) return false;
  if (left === right) return true;
  const leftNum = Number(left);
  const rightNum = Number(right);
  return Number.isFinite(leftNum) && Number.isFinite(rightNum) && leftNum === rightNum;
};

const calculateRowTax = (row, taxOptions = []) => {
  const qty = Number(row.quantity) || 0;
  const unit = Number(row.unitCost) || 0;
  const subTotal = hasValue(row?._poAuthoritativeSubTotal) && row?._poPrecisionEdited !== true
    ? Number(row._poAuthoritativeSubTotal)
    : hasValue(row?.subTotal) && row?.isNew !== true
      ? Number(row.subTotal)
    : qty * unit;
  const selectedTax = String(row.tax ?? '').trim();
  const taxOpt = selectedTax && Array.isArray(taxOptions)
    ? taxOptions.find((t) => taxIdEquals(t?.id, selectedTax))
    : null;
  let pcts = taxOpt
    ? parsePercents(taxOpt?.name || taxOpt?.label || taxOpt?.value || '')
    : parsePercents(selectedTax);
  if (pcts.length === 0 && selectedTax && !taxOpt && Array.isArray(taxOptions)) {
    const taxByName = taxOptions.find((t) =>
      String(t?.name || '').trim().toLowerCase() === selectedTax.toLowerCase()
    );
    if (taxByName) {
      pcts = parsePercents(taxByName?.name || taxByName?.label || taxByName?.value || '');
    }
  }
  return roundMoney(subTotal * (sumPct(pcts) / 100));
};

export const recalcBillingItemRow = (row, taxOptions = []) => {
  const qty = Number(row.quantity) || 0;
  const unit = Number(row.unitCost) || 0;
  const subTotal = hasValue(row?._poAuthoritativeSubTotal) && row?._poPrecisionEdited !== true
    ? roundMoney(row._poAuthoritativeSubTotal)
    : hasValue(row?.subTotal) && row?.isNew !== true
      ? roundMoney(row.subTotal)
    : roundMoney(qty * unit);
  const calculatedFromInputs = calculateRowTax(row, taxOptions);
  const hasManualOverride = hasValue(row?.taxOverrideAmount);
  const hasTaxSelection = hasValue(row?.tax);
  const hasExistingCalculatedTax = hasValue(row?.calculatedTaxAmount);
  const hasPersistedEffectiveTax = hasValue(row?.taxAmount);
  const calculatedTaxAmount = hasTaxSelection
    ? calculatedFromInputs
    : (hasExistingCalculatedTax
      ? roundMoney(row.calculatedTaxAmount)
      : (hasPersistedEffectiveTax ? roundMoney(row.taxAmount) : calculatedFromInputs));
  const effectiveTaxAmount = hasManualOverride
    ? roundMoney(row.taxOverrideAmount)
    : calculatedTaxAmount;
  const amount = roundMoney(subTotal + effectiveTaxAmount);

  return {
    ...row,
    subTotal,
    calculatedTaxAmount,
    taxAmount: effectiveTaxAmount,
    amount,
  };
};

export const createBlankBillingItemRow = (defaultTax = '') => ({
  code: '',
  itemName: '',
  quantity: 1,
  unitCost: 0,
  tax: normalizeTaxSelection(defaultTax),         // tax id inherited from bill default
  taxAmount: 0,
  taxOverrideAmount: '',
  calculatedTaxAmount: 0,
  subTotal: 0,
  amount: 0,
  isNew: true,
});

const rowsNeedFinancialNormalization = (rows = [], normalizedRows = []) =>
  rows.length !== normalizedRows.length ||
  rows.some((row, index) => {
    const normalized = normalizedRows[index] || {};
    return (
      row.subTotal !== normalized.subTotal ||
      row.amount !== normalized.amount ||
      row.taxAmount !== normalized.taxAmount ||
      row.calculatedTaxAmount !== normalized.calculatedTaxAmount
    );
  });

export const normalizeBillingItemRows = (rows = [], taxOptions = []) =>
  (Array.isArray(rows) ? rows : []).map((row) => recalcBillingItemRow(row, taxOptions));

export const updateBillingItemRows = (rows = [], idx, updater, taxOptions = []) =>
  (Array.isArray(rows) ? rows : []).map((row, index) => {
    if (index !== idx) return row;
    const updated = typeof updater === 'function' ? updater(row || {}) : updater;
    const quantityChanged = String(updated?.quantity ?? '') !== String(row?.quantity ?? '');
    const unitCostChanged = String(updated?.unitCost ?? '') !== String(row?.unitCost ?? '');
    const rowForRecalculation = quantityChanged || unitCostChanged
      ? clearAuthoritativePoSubtotal({ ...updated, subTotal: undefined })
      : updated;
    return recalcBillingItemRow(rowForRecalculation, taxOptions);
  });

export const removeBillingItemRowAt = (rows = [], idx) =>
  (Array.isArray(rows) ? rows : []).filter((_, index) => index !== idx);

export const applyDefaultTaxToBillingItems = (
  rows = [],
  defaultTax = '',
  taxOptions = [],
  { allowDetailedItemTaxCustomization = true } = {}
) => {
  const normalizedDefaultTax = normalizeTaxSelection(defaultTax);
  if (!normalizedDefaultTax) return Array.isArray(rows) ? rows : [];

  let changed = false;
  const nextRows = (Array.isArray(rows) ? rows : []).map((row) => {
    if (row?._included === false) return row;

    const hasExistingTax = Boolean(normalizeTaxSelection(row?.tax));
    const shouldApply = allowDetailedItemTaxCustomization ? !hasExistingTax : true;
    if (!shouldApply) return row;

    const refreshed = recalcBillingItemRow(
      { ...row, tax: normalizedDefaultTax },
      taxOptions
    );

    if (
      String(row?.tax ?? '') !== String(normalizedDefaultTax) ||
      refreshed.taxAmount !== row?.taxAmount ||
      refreshed.calculatedTaxAmount !== row?.calculatedTaxAmount ||
      refreshed.amount !== row?.amount
    ) {
      changed = true;
      return refreshed;
    }

    return row;
  });

  return changed ? nextRows : rows;
};

export const includedBillingItems = (rows = []) =>
  (Array.isArray(rows) ? rows : []).filter((row) => row?._included !== false);

const cleanVariantPart = (value) => String(value ?? '').trim();

export const getBillingItemVariant = (item = {}) => {
  const structured = item?._variantDetails || {};
  const parts = [
    structured.style ?? item.styleNumber ?? item.style ?? item.SKU ?? item.sku ?? item.itemCode,
    structured.size ?? item.Size ?? item.size,
    structured.color ?? item.Color ?? item.color ?? item.Colour ?? item.colour,
  ].map(cleanVariantPart).filter(Boolean);
  if (parts.length) return parts.join(' / ');

  const match = cleanVariantPart(item.itemName).match(/\(([^()]*)\)\s*$/);
  const fallback = cleanVariantPart(match?.[1]);
  return fallback.includes('/') ? fallback : '—';
};

export const getBillingItemDisplayName = (item = {}) => {
  const fullName = cleanVariantPart(item.itemName);
  const fallbackVariant = getBillingItemVariant({ itemName: fullName });
  if (fallbackVariant === '—') return fullName;
  return fullName.replace(/\s*\([^()]*\)\s*$/, '').trim() || fullName;
};

const BillingItemsTable = ({
  singleBillData,
  taxOptions,
  setSingleBillData,
  financeCodes = [],
  defaultTax = '',
  allowDetailedItemTaxCustomization = true,
}) => {
  const items = useMemo(
    () => (Array.isArray(singleBillData) ? singleBillData : []),
    [singleBillData]
  );
  const setItems = useCallback((updater) => {
    setSingleBillData((prevRows) => {
      const rows = Array.isArray(prevRows) ? prevRows : [];
      const next = typeof updater === 'function' ? updater(rows) : updater;
      return Array.isArray(next) ? next : [];
    });
  }, [setSingleBillData]);

  // Expand/collapse "Breakdown" area (optional)
  const [expandedItemId, setExpandedItemId] = useState(null);
  const [focusedItemIndex, setFocusedItemIndex] = useState(null);

  const addBillItem = () => {
    setItems((rows) => [...rows, createBlankBillingItemRow(defaultTax)]);
    setExpandedItemId(items.length);       // open the newly added row
  };

  const removeBillItem = (idx) => {
    setItems((rows) => removeBillingItemRowAt(rows, idx));
    if (expandedItemId === idx) setExpandedItemId(null);
  };

  const updateRow = (idx, updater) => {
    setItems((rows) => updateBillingItemRows(rows, idx, updater, taxOptions));
  };

  useEffect(() => {
    setItems((rows) => applyDefaultTaxToBillingItems(
      rows,
      defaultTax,
      taxOptions,
      { allowDetailedItemTaxCustomization }
    ));
  }, [allowDetailedItemTaxCustomization, defaultTax, setItems, taxOptions]);

  const onChangeField = (idx, name, value) => {
    updateRow(idx, (row) => ({ ...row, [name]: value }));
  };

  const onTextField = (e, idx) => {
    const { name, value } = e.target;
    onChangeField(idx, name, name === 'taxOverrideAmount' && value === '' ? '' : value);
  };

  const onDropdown = (payload, idx, field, optionsArr) => {
    const selected = pickSelected(payload);
    // try to find label/name for display (esp. for tax field)
    const opt = Array.isArray(optionsArr)
      ? (optionsArr.find((o) =>
          taxIdEquals(o?.id, selected) ||
          String(o?.value ?? '') === String(selected ?? '') ||
          String(o?.name ?? '') === String(selected ?? '') ||
          String(o?.label ?? '') === String(selected ?? '')
        ) || null)
      : null;

    if (field === 'code') {
      updateRow(idx, (row) => {
        const previousOption = Array.isArray(optionsArr)
          ? optionsArr.find((option) => String(option?.id ?? option?.value ?? '') === String(row?.code ?? ''))
          : null;
        return applyFinanceCodeSelection(row, opt, selected, previousOption);
      });
    } else if (field === 'tax') {
      // store dropdown id so CustomDropdown can display selected state reliably
      onChangeField(idx, 'tax', opt?.id ?? opt?.value ?? selected ?? '');
    } else {
      // generic
      onChangeField(idx, field, opt?.value ?? opt?.id ?? selected ?? '');
    }
  };

  const normalized = useMemo(
    () => normalizeBillingItemRows(items, taxOptions),
    [items, taxOptions]
  );

  useEffect(() => {
    if (rowsNeedFinancialNormalization(items, normalized)) {
      setItems(normalized);
    }
  }, [items, normalized, setItems]);

  // Layout: fixed table + explicit column widths so it fits in the modal/popup
  const col = {
    check:  '4%',
    code:   '11%',
    name:   '23%',
    variant:'18%',
    qty:    '7%',
    unit:   '10%',
    sub:    '10%',
    tot:    '10%',
    act:    '7%',
  };

  const fieldWrapSx = { width: '100%', display: 'flex' }; // inputs fill cell
  const showFinanceCodeWarning = !hasEnabledBillFinanceCodes(financeCodes);
  const resolveTaxSelectedValue = (rawTax) => {
    const value = String(rawTax ?? '').trim();
    if (!value) return '';
    const byId = taxOptions?.find((t) => taxIdEquals(t?.id, value));
    if (byId) return String(byId.id);
    const byName = taxOptions?.find((t) => String(t?.name || '').trim().toLowerCase() === value.toLowerCase());
    if (byName) return String(byName.id);
    return value;
  };

  return (
    <Box mt={4}>
      <Box mt={2} mb={1} display="flex" justifyContent="space-between" alignItems="center">
        <Typography sx={{ color: '#6099a4', fontWeight: 600 }}>
          Bill Items
        </Typography>
        <Button variant="contained" sx={{ backgroundColor: '#6099a4' }} onClick={addBillItem}>
          Add Bill Item
        </Button>
      </Box>
      {showFinanceCodeWarning ? (
        <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'warning.main' }}>
          No bill finance codes are configured. Add them in Defaults &gt; Finance Codes or install the Billing Starter Pack.
        </Typography>
      ) : null}
      <TableContainer component={Paper} sx={{ border: '1px solid #eee' }}>
        <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 1050 }}>
          <TableHead sx={{ backgroundColor: '#f9f9f9' }}>
            <TableRow>
              <TableCell sx={{ width: col.check }} />
              <TableCell sx={{ width: col.code }}><strong>Code</strong></TableCell>
              <TableCell sx={{ width: col.name }}><strong>Item Name</strong></TableCell>
              <TableCell sx={{ width: col.variant }}><strong>Variant</strong></TableCell>
              <TableCell sx={{ width: col.qty }}><strong>Qty</strong></TableCell>
              <TableCell sx={{ width: col.unit }}><strong>Unit Cost</strong></TableCell>
              <TableCell sx={{ width: col.sub }}><strong>Subtotal</strong></TableCell>
              <TableCell sx={{ width: col.tot }}><strong>Total</strong></TableCell>
              <TableCell sx={{ width: col.act }} align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {items?.length > 0 ? items.map((item, index) => {
              const sub = money2(item.subTotal);
              const tot = money2(item.amount);
              const hasManualOverride = hasValue(item?.taxOverrideAmount);
              const variant = getBillingItemVariant(item);
              const displayName = getBillingItemDisplayName(item);
              const codeOptions = buildFinanceCodeDropdownOptions(financeCodes, item.code);
              return (
                <React.Fragment key={item.id || `row-${index}`}>
                  <TableRow>
                    <TableCell sx={{ width: col.check }}>
                      <Checkbox
                        checked={item?._included !== false}
                        onChange={(event) => onChangeField(index, '_included', event.target.checked)}
                        inputProps={{ 'aria-label': `Include ${displayName || `bill line ${index + 1}`}` }}
                      />
                    </TableCell>

                    {/* Code */}
                    <TableCell sx={{ width: col.code }}>
                      <Box sx={fieldWrapSx}>
                        <CustomDropdown
                          name="code"
                          options={codeOptions}
                          handleSelect={(payload) => onDropdown(payload, index, 'code', codeOptions)}
                          selectedValue={codeOptions.find(o => o.id === item.code)?.id || item.code || ''}
                          placeholder="Code"
                        />
                      </Box>
                    </TableCell>

                    {/* Item Name */}
                    <TableCell sx={{ width: col.name }}>
                      <Box sx={fieldWrapSx}>
                        <CustomTextField
                          name="itemName"
                          value={focusedItemIndex === index ? (item.itemName ?? '') : displayName}
                          placeholder="Item/Charge name"
                          handleChange={(e) => onTextField(e, index)}
                          onFocus={() => setFocusedItemIndex(index)}
                          onBlur={() => setFocusedItemIndex(null)}
                          inputProps={{ 'aria-label': `Item: ${displayName}` }}
                        />
                      </Box>
                    </TableCell>

                    <TableCell sx={{ width: col.variant }}>
                      <Typography variant="body2" title={variant} noWrap>{variant}</Typography>
                    </TableCell>

                    {/* Qty */}
                    <TableCell sx={{ width: col.qty }}>
                      <Box sx={fieldWrapSx}>
                        <CustomTextField
                          name="quantity"
                          value={item.quantity ?? 0}
                          type="number"
                          handleChange={(e) => onTextField(e, index)}
                        />
                      </Box>
                    </TableCell>

                    {/* Unit Cost */}
                    <TableCell sx={{ width: col.unit }}>
                      <Box sx={fieldWrapSx}>
                        <CustomTextField
                          name="unitCost"
                          value={item.unitCost ?? 0}
                          type="number"
                          placeholder="0.00"
                          handleChange={(e) => onTextField(e, index)}
                        />
                      </Box>
                    </TableCell>

                    {/* Subtotal (read-only) */}
                    <TableCell sx={{ width: col.sub }}>
                      <Box sx={fieldWrapSx}>
                        <CustomTextField name="subTotal" value={sub} disabled />
                      </Box>
                    </TableCell>

                    {/* Total (read-only) */}
                    <TableCell sx={{ width: col.tot }}>
                      <Box sx={fieldWrapSx}>
                        <CustomTextField name="amount" value={tot} disabled />
                      </Box>
                    </TableCell>

                    {/* Row changes remain local; the modal-level Save persists them. */}
                    <TableCell align="right" sx={{ width: col.act, whiteSpace: 'nowrap' }}>
                      <IconButton
                        aria-label={expandedItemId === index ? 'Hide line details' : 'Show line details'}
                        onClick={() => setExpandedItemId(expandedItemId === index ? null : index)}
                        size="small"
                      >
                        {expandedItemId === index ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => removeBillItem(index)}
                        sx={{ ml: 1 }}
                        size="large">
                        <DeleteOutlineIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  {/* Breakdown row (optional info) */}
                  {expandedItemId === index && (
                    <TableRow>
                      <TableCell colSpan={9}>
                        <Box sx={{ p: 2, background: '#fafafa', border: '1px dashed #e0e0e0' }}>
                          <Typography variant="body2" sx={{ mb: 1, color: '#666' }}>
                            Breakdown
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block' }}>
                            Qty × Unit = Subtotal: {item.quantity || 0} × {money2(item.unitCost)} = {money2(item.subTotal)}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block' }}>
                            Tax: {hasManualOverride ? `$${money2(item.taxOverrideAmount)} (override)` : `$${money2(item.taxAmount ?? item.calculatedTaxAmount)}`}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block' }}>
                            Total: {money2(item.amount)}
                          </Typography>
                          {allowDetailedItemTaxCustomization ? (
                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(180px, 1fr) minmax(180px, 1fr)' }, gap: 2, mt: 2, maxWidth: 600 }}>
                            <Box>
                              <Typography variant="caption">Tax</Typography>
                              <CustomDropdown
                                name="tax"
                                options={taxOptions}
                                handleSelect={(payload) => onDropdown(payload, index, 'tax', taxOptions)}
                                selectedValue={resolveTaxSelectedValue(item.tax)}
                                placeholder="Select Tax"
                              />
                            </Box>
                            <Box>
                              <Typography variant="caption">Tax Override</Typography>
                              <CustomTextField
                                name="taxOverrideAmount"
                                value={item.taxOverrideAmount ?? ''}
                                type="number"
                                placeholder="Override $"
                                handleChange={(e) => onTextField(e, index)}
                              />
                              {hasManualOverride ? (
                                <Button size="small" variant="text" onClick={() => onChangeField(index, 'taxOverrideAmount', '')}>
                                  Reset line tax override
                                </Button>
                              ) : null}
                            </Box>
                          </Box>
                          ) : null}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            }) : (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No bill items yet.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default BillingItemsTable;
